import { FaroChipFilter, FaroMenu, SingleSelectItem } from "@faro-lotv/flat-ui";
import { Box } from "@mui/system";
import { useMemo, useState, type JSX } from "react";

export enum Dates {
  NoDate = "No date" /** For annotations with no due date defined */,
  BeforeToday = "Before today",
  Today = "Today",
  Tomorrow = "Tomorrow",
  Next7Days = "Next 7 days",
  Next30Days = "Next 30 days",
  Next90Days = "Next 90 days",
}

export type DateFilterProps = {
  /** The currently selected date */
  selectedDate?: Dates;

  /** Callback executed when the selection changes */
  onSelectedDateChanged(date: Dates | undefined): void;
};

/** @returns A chip with a popup that can be used to select a due date option */
export function DateFilter({
  selectedDate,
  onSelectedDateChanged,
}: DateFilterProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement>();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const options = useMemo(
    () =>
      Object.values(Dates).map((date) => ({
        key: date,
        value: date,
        label: (
          <SingleSelectItem
            key={date}
            onClick={() => {
              // Allow to deselect by clicking the same date again
              const newDate = selectedDate === date ? undefined : date;
              onSelectedDateChanged(newDate);
            }}
            label={date}
            value={date}
            selectedValue={selectedDate}
          />
        ),
      })),
    [selectedDate, onSelectedDateChanged],
  );

  const selectedOption = useMemo(
    () => options.filter((option) => option.key === selectedDate),
    [selectedDate, options],
  );

  return (
    <Box ref={setAnchorEl} component="div">
      <FaroChipFilter
        label="Due date"
        selected={isPopoverOpen || !!selectedOption.length}
        count={selectedOption.length ? 1 : undefined}
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
      />

      {anchorEl && (
        <FaroMenu
          anchorEl={anchorEl}
          open={isPopoverOpen}
          onClose={() => setIsPopoverOpen(false)}
          sx={{ mt: 0.5 }}
        >
          {options.map((option) => option.label)}
        </FaroMenu>
      )}
    </Box>
  );
}
